import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Calendar as CalendarIcon,
  Clock,
  Users,
  Home,
  Plus,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Trash2,
  X,
  MapPin,
  FileText,
  RefreshCw,
  Palette,
  Filter
} from "lucide-react";
import api from "../../api";
import toast from "react-hot-toast";

// Event categories the user can color-code. Each event is mapped to one of these
// (see getEventCategory). Colors are user-overridable and persisted in localStorage.
const EVENT_CATEGORIES = [
  { key: "event", label: "Events" },
  { key: "holiday", label: "Holidays" },
  { key: "open_home", label: "Open Homes" },
  { key: "task", label: "Tasks" },
  { key: "reminder", label: "Call-backs & Reminders" },
];

const DEFAULT_EVENT_COLORS = {
  event: "#55C5D0",
  holiday: "#E02424",
  open_home: "#1E8E3E",
  task: "#B45309",
  reminder: "#7C3AED",
};

const EVENT_COLORS_STORAGE_KEY = "calendarEventColors";
const CALENDAR_PREFS_STORAGE_KEY = "calendarVisibilityPrefs";

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("month"); // month, week, day
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  // True until the initial connection-status check resolves, so we can show a
  // spinner instead of flashing the "Connect" prompt for already-connected users.
  const [checkingConnection, setCheckingConnection] = useState(true);
  // True until the first events fetch finishes, so we show a spinner instead of
  // an empty calendar grid. Later month navigations don't flip this back.
  const [initialEventsLoad, setInitialEventsLoad] = useState(true);
  // Timestamp of the last successful sync (Date) — shown next to the Sync button.
  const [lastSynced, setLastSynced] = useState(null);
  // True when the backend reports the Google connection can no longer be used
  // (token expired/revoked) — we prompt the user to reconnect instead of
  // silently showing an empty calendar.
  const [needsReconnect, setNeedsReconnect] = useState(false);

  // User-configurable colors per event category, persisted in localStorage.
  const [eventColors, setEventColors] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(EVENT_COLORS_STORAGE_KEY));
      return saved ? { ...DEFAULT_EVENT_COLORS, ...saved } : DEFAULT_EVENT_COLORS;
    } catch {
      return DEFAULT_EVENT_COLORS;
    }
  });
  const [showColorSettings, setShowColorSettings] = useState(false);
  // Calendars (by id) the user has unchecked in the calendar filter, hidden from
  // the grid. Mirrors Google Calendar's per-calendar checkboxes so an agent who
  // has other people's shared calendars can hide them. Persisted in localStorage.
  // The user's Google calendar list as reported by the backend (own + shared),
  // used to populate the per-calendar filter even for calendars with no events.
  const [calendars, setCalendars] = useState([]);
  // Explicit per-calendar show/hide choices the user has made in the CRM filter,
  // keyed by calendar id ({ id: true|false }). When a calendar isn't in here we
  // fall back to Google's own checkbox state (the `selected` flag). Persisted.
  const [calendarPrefs, setCalendarPrefs] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(CALENDAR_PREFS_STORAGE_KEY));
      return saved && typeof saved === "object" ? saved : {};
    } catch {
      return {};
    }
  });
  const [showCalendarFilter, setShowCalendarFilter] = useState(false);

  // Persist the per-calendar visibility choices so they survive reloads.
  useEffect(() => {
    try {
      localStorage.setItem(CALENDAR_PREFS_STORAGE_KEY, JSON.stringify(calendarPrefs));
    } catch {
      /* ignore quota/serialization errors */
    }
  }, [calendarPrefs]);

  // Guards the create/update submit so a double-click can't create duplicates.
  // The ref is the synchronous guard (state updates are async); the state drives
  // the button's disabled/label.
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [showEventModal, setShowEventModal] = useState(false);
  // Date whose events are shown in the day-events modal (month view click).
  const [dayModalDate, setDayModalDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalMode, setModalMode] = useState("create"); // create, edit, view

  // Form state
  const [eventForm, setEventForm] = useState({
    summary: "",
    start: "",
    duration_minutes: 60,
    location: "",
    description: "",
  });

  // Brand Colors
  const brand = {
    teal: "#55C5D0",
    tealHover: "#3bb3be",
    tealLight: "#E0F7FA",
  };

  // Check calendar connection status
  useEffect(() => {
    checkConnectionStatus();
  }, []);

  // Fetch events when date or connection changes
  useEffect(() => {
    if (isConnected) {
      fetchEvents();
    }
  }, [currentDate, isConnected]);

  // Keep the calendar in sync automatically: poll every 60s and refresh whenever
  // the user returns to the tab, so events added in Google Calendar show up here
  // without a manual reload. These are silent (no spinner / no toast).
  useEffect(() => {
    if (!isConnected) return;
    const interval = setInterval(() => {
      if (!document.hidden) fetchEvents({ silent: true });
    }, 60000);
    const onFocus = () => fetchEvents({ silent: true });
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  // Persist event colors whenever the user changes them.
  useEffect(() => {
    try {
      localStorage.setItem(EVENT_COLORS_STORAGE_KEY, JSON.stringify(eventColors));
    } catch {
      /* ignore storage errors */
    }
  }, [eventColors]);

  // Map an event to its category, then to the user's chosen color.
  const getEventCategory = (event) => {
    if (event.source === "crm_task" || event.event_type === "task") return "task";
    if (event.source === "crm_reminder" || event.event_type === "reminder") return "reminder";
    if (event.source === "crm_inspection" || event.event_type === "open_home") return "open_home";
    const cid = (event.calendar_id || "").toLowerCase();
    if (cid.includes("holiday")) return "holiday";
    return "event";
  };
  const getEventColor = (event) =>
    eventColors[getEventCategory(event)] || eventColors.event;

  // Only real, writable Google events of the user's can be edited/deleted here.
  // Public holidays (read-only calendars) and CRM occasions (managed in their own
  // sections) must not show edit/delete actions.
  const isEventEditable = (event) => {
    if (getEventCategory(event) === "holiday") return false;
    if (event.source && event.source.startsWith("crm")) return false;
    const role = (event.access_role || "").toLowerCase();
    if (role && role !== "owner" && role !== "writer") return false;
    return true;
  };

  const checkConnectionStatus = async () => {
    try {
      const response = await api.get("google/calendar/status/");
      setIsConnected(response.data.connected);
    } catch (error) {
      console.error("Error checking calendar status:", error);
      setIsConnected(false);
    } finally {
      setCheckingConnection(false);
    }
  };

  // Fetch (sync) events from Google Calendar + CRM occasions.
  //  - silent: background refresh (polling / focus) — don't toggle the loading
  //    spinner so the grid doesn't flicker.
  //  - manual: user clicked "Sync" — show a success/empty toast for feedback.
  const fetchEvents = async ({ silent = false, manual = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      // Cache-bust so we always pull fresh events straight from Google rather
      // than any deduplicated/cached response.
      const response = await api.get("google/calendar/events/", {
        params: { _ts: Date.now() },
      });

      // The connection can silently go stale (Google token expired/revoked); the
      // backend flags this so we can prompt a reconnect instead of showing empty.
      const reconnect = !!response.data?.needs_reconnect;
      setNeedsReconnect(reconnect);

      // Calendar list for the show/hide filter (own + shared calendars). Keeping
      // the previous list when a (silent) refresh returns none avoids the filter
      // flickering empty between polls.
      if (Array.isArray(response.data?.calendars) && response.data.calendars.length) {
        setCalendars(response.data.calendars);
      }

      // Normalize event data
      const eventData = Array.isArray(response.data)
        ? response.data
        : response.data.events || [];

      // Create a unique ID generator to avoid duplicates
      const seenIds = new Set();

      const normalizedEvents = eventData.map((e, idx) => {
        // Get the original ID
        const originalId = e.id || e.event_id;

        // Create a base unique ID
        let uniqueId = originalId || `event-${idx}`;

        // If this ID has been seen before, make it unique by adding index and random component
        if (seenIds.has(uniqueId)) {
          uniqueId = `${uniqueId}-${idx}-${Math.random().toString(36).substr(2, 9)}`;
        }

        seenIds.add(uniqueId);

        return {
          id: uniqueId,
          title: e.title || e.summary || "Untitled Event",
          start: e.start || e.start_time || e.startDateTime,
          end: e.end || e.end_time || e.endDateTime,
          location: e.location || "",
          description: e.description || "",
          status: e.status || "",
          htmlLink: e.htmlLink || "",
          attendees: Array.isArray(e.attendees) ? e.attendees : [],
          originalId: originalId, // Keep original ID for API calls
          // Category metadata used for color-coding.
          source: e.source || "",
          event_type: e.event_type || "",
          calendar_id: e.calendar_id || "",
          calendar_name: e.calendar_name || "",
          access_role: e.access_role || "",
        };
      });

      setEvents(normalizedEvents);
      setLastSynced(new Date());

      if (manual) {
        if (reconnect) {
          toast.error("Google Calendar needs to be reconnected to sync");
        } else {
          toast.success(
            normalizedEvents.length
              ? `Synced ${normalizedEvents.length} event${normalizedEvents.length === 1 ? "" : "s"}`
              : "Calendar synced — no events found"
          );
        }
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      if (!silent) toast.error("Failed to sync events");
    } finally {
      if (!silent) setLoading(false);
      setInitialEventsLoad(false);
    }
  };

  const handleConnectCalendar = async () => {
    try {
      const callbackUrl = `${window.location.origin}/google-calender/callback`;
      const statePayload = btoa(JSON.stringify({ redirect: "/calendar" }));

      const response = await api.get("google/calendar/oauth/start/", {
        params: {
          callback_url: callbackUrl,
          state: statePayload,
        },
      });

      const authUrl = response.data?.authorization_url ||
        response.data?.auth_url ||
        response.data?.url;

      if (authUrl) {
        window.location.href = authUrl;
      }
    } catch (error) {
      console.error("Error connecting calendar:", error);
      toast.error("Failed to connect Google Calendar");
    }
  };

  const handleCreateEvent = async () => {
    if (submittingRef.current) return; // prevent double-submit creating duplicates
    if (!eventForm.summary || !eventForm.start) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      submittingRef.current = true;
      setSubmitting(true);
      await api.post("google/calendar/events/create/", eventForm);
      toast.success("Event created successfully!");
      setShowEventModal(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      console.error("Error creating event:", error);
      toast.error(error.response?.data?.detail || "Failed to create event");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleUpdateEvent = async () => {
    if (submittingRef.current) return; // prevent double-submit
    if (selectedEvent && !isEventEditable(selectedEvent)) {
      toast.error("This event is read-only and can't be edited");
      return;
    }
    if (!eventForm.summary || !eventForm.start) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      submittingRef.current = true;
      setSubmitting(true);
      const eventId = selectedEvent.originalId || selectedEvent.id;
      await api.put(`google/calendar/events/${eventId}/update/`, eventForm);
      toast.success("Event updated successfully!");
      setShowEventModal(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      console.error("Error updating event:", error);
      toast.error(error.response?.data?.detail || "Failed to update event");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };


  const handleDeleteEvent = async (event) => {
    if (event && !isEventEditable(event)) {
      toast.error("This event is read-only and can't be deleted");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      const eventId = event.originalId || event.id || event;
      await api.delete(`google/calendar/events/${eventId}/delete/`);
      toast.success("Event deleted successfully!");
      setShowEventModal(false);
      fetchEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error(error.response?.data?.detail || "Failed to delete event");
    }
  };

  const handleCreateTaskFromEvent = async (event) => {
    try {
      // Determine task type based on event title/description
      let taskType = "general";
      const titleLower = event.title.toLowerCase();
      if (titleLower.includes("meeting") || titleLower.includes("appointment")) {
        taskType = "meeting";
      } else if (titleLower.includes("viewing") || titleLower.includes("showing")) {
        taskType = "viewing";
      } else if (titleLower.includes("inspection") || titleLower.includes("ofi")) {
        taskType = "inspection";
      } else if (titleLower.includes("call")) {
        taskType = "call";
      }

      const taskData = {
        task_name: event.title,
        description: event.description || `Calendar event: ${event.title}`,
        due_date: new Date(event.start).toISOString().split('T')[0],
        task_type: taskType,
        priority: "medium",
        status: "not_started",
        calendar_event_id: event.id,
      };

      const response = await api.post("tasks/", taskData);
      toast.success("Task created from event!");
      setShowEventModal(false);
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error(error.response?.data?.detail || "Failed to create task");
    }
  };

  const openCreateModal = () => {
    resetForm();
    setModalMode("create");
    setSelectedEvent(null);

    // Set default start time to next hour
    const nextHour = new Date();
    nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
    setEventForm({
      ...eventForm,
      start: nextHour.toISOString().slice(0, 16),
    });

    setShowEventModal(true);
  };

  // Format a Date for a datetime-local input in LOCAL time (not UTC).
  const toLocalInputValue = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Open the create modal pre-filled to a specific date (used from the day modal).
  const openCreateModalOnDate = (dateObj) => {
    resetForm();
    setModalMode("create");
    setSelectedEvent(null);
    const d = new Date(dateObj);
    d.setHours(9, 0, 0, 0);
    setEventForm((f) => ({ ...f, start: toLocalInputValue(d) }));
    setShowEventModal(true);
  };

  const openEditModal = (event) => {
    setSelectedEvent(event);
    setModalMode("edit");

    // Parse existing event data
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);
    const duration = Math.round((endDate - startDate) / 60000); // minutes

    setEventForm({
      summary: event.title,
      start: startDate.toISOString().slice(0, 16),
      duration_minutes: duration,
      location: event.location || "",
      description: event.description || "",
    });

    setShowEventModal(true);
  };

  const openViewModal = (event) => {
    setSelectedEvent(event);
    setModalMode("view");
    setShowEventModal(true);
  };

  const resetForm = () => {
    setEventForm({
      summary: "",
      start: "",
      duration_minutes: 60,
      location: "",
      description: "",
    });
  };

  // Calendar navigation
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateDay = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Calendar grid helpers
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getMonthGrid = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  // Compare in LOCAL time to match how the grid cells are built. Using
  // toISOString() here bucketed events by their UTC date, which pushed
  // late-night events onto the wrong day (or out of the month entirely).
  // Calendars to list in the filter: prefer the backend's full list (own +
  // shared, including ones with no events); fall back to deriving from events if
  // the backend didn't supply it (older API / service-account path).
  const calendarList = useMemo(() => {
    if (calendars.length > 0) {
      return [...calendars].sort((a, b) => {
        if (a.primary !== b.primary) return a.primary ? -1 : 1; // own calendar first
        return (a.name || "").localeCompare(b.name || "");
      });
    }
    const map = new Map();
    for (const e of events) {
      if (!e.calendar_id) continue;
      if (!map.has(e.calendar_id)) {
        map.set(e.calendar_id, {
          id: e.calendar_id,
          name: e.calendar_name || e.calendar_id,
          selected: true,
          primary: false,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [calendars, events]);

  const calendarById = useMemo(() => {
    const m = new Map();
    for (const c of calendarList) m.set(c.id, c);
    return m;
  }, [calendarList]);

  // Effective visibility for a calendar: the user's explicit CRM choice if they
  // made one, otherwise Google's own checkbox state (`selected`). A calendar the
  // user hid in Google (e.g. a colleague's shared one) starts hidden here too.
  const isCalendarShown = (id) => {
    if (Object.prototype.hasOwnProperty.call(calendarPrefs, id)) return calendarPrefs[id];
    const c = calendarById.get(id);
    if (c) return c.primary ? true : c.selected !== false;
    return true;
  };

  // An event shows unless its calendar is hidden. CRM occasions (no calendar_id)
  // are always visible.
  const isEventVisible = (e) => !e.calendar_id || isCalendarShown(e.calendar_id);

  const toggleCalendar = (id) =>
    setCalendarPrefs((prev) => ({ ...prev, [id]: !isCalendarShown(id) }));

  const getEventsForDate = (dateObj) =>
    events
      .filter((event) => {
        if (!event.start) return false;
        if (!isEventVisible(event)) return false;
        const d = new Date(event.start);
        return (
          d.getFullYear() === dateObj.getFullYear() &&
          d.getMonth() === dateObj.getMonth() &&
          d.getDate() === dateObj.getDate()
        );
      })
      .sort((a, b) => new Date(a.start) - new Date(b.start));

  const getEventsForDay = (day) => {
    if (!day) return [];
    return getEventsForDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
  };

  // Open the day-events modal for a given day-of-month in the current view month.
  const openDayModal = (day) => {
    if (!day) return;
    setDayModalDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Week boundaries (Sun–Sat) for the week containing `date`, in local time.
  const startOfWeek = (date) => {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
  };
  const endOfWeek = (date) => {
    const e = startOfWeek(date);
    e.setDate(e.getDate() + 6);
    e.setHours(23, 59, 59, 999);
    return e;
  };
  const isSameLocalDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  // Events shown in the Week/Day list views, filtered to the current period and
  // sorted by start time. (Month uses the grid via getEventsForDay instead.)
  const getListEvents = () => {
    let filtered = events.filter((e) => !!e.start && isEventVisible(e));
    if (view === "day") {
      filtered = filtered.filter((e) => isSameLocalDay(new Date(e.start), currentDate));
    } else if (view === "week") {
      const ws = startOfWeek(currentDate);
      const we = endOfWeek(currentDate);
      filtered = filtered.filter((e) => {
        const d = new Date(e.start);
        return d >= ws && d <= we;
      });
    }
    return filtered.sort((a, b) => new Date(a.start) - new Date(b.start));
  };

  const monthGrid = getMonthGrid();
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Header title reflects the active view so navigation feels responsive.
  const viewTitle =
    view === "day"
      ? currentDate.toLocaleDateString('en-US', {
          weekday: 'short', month: 'long', day: 'numeric', year: 'numeric',
        })
      : view === "week"
      ? `${startOfWeek(currentDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${endOfWeek(currentDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
      : monthName;

  // While the connection check (or the first events load for a connected user)
  // is still in flight, show a spinner instead of flashing the Connect prompt or
  // an empty calendar grid.
  if (checkingConnection || (isConnected && initialEventsLoad)) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <svg
            className="animate-spin h-8 w-8"
            style={{ color: brand.teal }}
            viewBox="0 0 24 24"
            role="status"
            aria-label="Loading"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4A4 4 0 008 12H4z" />
          </svg>
          <span className="text-sm">
            {checkingConnection ? "Checking calendar connection…" : "Loading your calendar…"}
          </span>
        </div>
      </div>
    );
  }

  // If not connected, show connection prompt
  if (!isConnected) {
    return (
      <div className="min-h-screen bg-white p-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
            <div
              className="mx-auto mb-4 h-16 w-16 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${brand.teal}15` }}
            >
              <CalendarIcon className="w-8 h-8" style={{ color: brand.teal }} />
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Connect Your Calendar
            </h2>
            <p className="text-gray-600 mb-6">
              Link your Google Calendar to manage events and appointments
            </p>

            <button
              onClick={handleConnectCalendar}
              className="px-6 py-3 rounded-xl text-white font-medium shadow-sm hover:shadow transition-all duration-200"
              style={{ backgroundColor: brand.teal }}
            >
              Connect Google Calendar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6 md:p-8 overflow-x-hidden max-w-full">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-center gap-4 w-full max-w-full">
        <div className="text-center sm:text-left w-full sm:w-auto">
          <h2 className="text-2xl font-light" style={{ color: brand.teal }}>
            Calendar & Scheduling
          </h2>
          <p className="text-gray-600">Manage your appointments and events</p>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Calendar visibility filter — Google-style per-calendar checkboxes.
              Only shown when the user actually has Google calendars (e.g. their
              own plus any shared with them) so they can hide other people's. */}
          {calendarList.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowCalendarFilter((s) => !s)}
                title="Show/hide calendars"
                aria-label="Show/hide calendars"
                aria-expanded={showCalendarFilter}
                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-xl border font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200 ${
                  showCalendarFilter ? "bg-gray-50" : ""
                }`}
              >
                <Filter className="w-5 h-5" />
                <span className="sm:hidden">Calendars</span>
              </button>

              {showCalendarFilter && (
                <>
                  {/* Click-away overlay */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowCalendarFilter(false)}
                  />
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl border border-gray-200 shadow-lg z-50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-900">Calendars</h4>
                      {Object.keys(calendarPrefs).length > 0 && (
                        <button
                          onClick={() => setCalendarPrefs({})}
                          title="Revert to your Google show/hide settings"
                          className="text-xs text-gray-500 hover:text-gray-700 underline"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {calendarList.map((cal) => (
                        <label
                          key={cal.id}
                          className="flex items-center gap-2 cursor-pointer text-sm text-gray-700"
                        >
                          <input
                            type="checkbox"
                            checked={isCalendarShown(cal.id)}
                            onChange={() => toggleCalendar(cal.id)}
                            className="h-4 w-4 rounded border-gray-300"
                            style={{ accentColor: brand.teal }}
                          />
                          <span className="truncate" title={cal.name}>
                            {cal.name}{cal.primary ? " (mine)" : ""}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Event color settings */}
          <div className="relative">
            <button
              onClick={() => setShowColorSettings((s) => !s)}
              title="Event colors"
              aria-label="Event colors"
              aria-expanded={showColorSettings}
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-xl border font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200 ${
                showColorSettings ? "bg-gray-50" : ""
              }`}
            >
              <Palette className="w-5 h-5" />
              <span className="sm:hidden">Colors</span>
            </button>

            {showColorSettings && (
              <>
                {/* Click-away overlay */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowColorSettings(false)}
                />
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl border border-gray-200 shadow-lg z-50 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-900">Event Colors</h4>
                    <button
                      onClick={() => setEventColors(DEFAULT_EVENT_COLORS)}
                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      Reset
                    </button>
                  </div>
                  <div className="space-y-2.5">
                    {EVENT_CATEGORIES.map((cat) => (
                      <div key={cat.key} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block w-3 h-3 rounded-full"
                            style={{ backgroundColor: eventColors[cat.key] }}
                          />
                          <span className="text-sm text-gray-700">{cat.label}</span>
                        </div>
                        <input
                          type="color"
                          value={eventColors[cat.key]}
                          onChange={(e) =>
                            setEventColors((prev) => ({ ...prev, [cat.key]: e.target.value }))
                          }
                          className="h-7 w-10 rounded cursor-pointer border border-gray-200 bg-white p-0.5"
                          aria-label={`Color for ${cat.label}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-col items-center sm:items-end">
            <button
              onClick={() => fetchEvents({ manual: true })}
              disabled={loading}
              title="Sync with Google Calendar"
              aria-label="Sync with Google Calendar"
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-xl border font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200 ${
                loading ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
              <span>{loading ? "Syncing…" : "Sync"}</span>
            </button>
            {lastSynced && (
              <span className="mt-1 text-[10px] text-gray-400 hidden sm:block">
                Synced {lastSynced.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </span>
            )}
            {/* Always-available reconnect: lets a user re-auth or switch Google
                accounts even when no error is detected (e.g. they connected the
                wrong account, so events come back but not the ones they expect). */}
            <button
              onClick={handleConnectCalendar}
              title="Reconnect or switch Google account"
              className="mt-1 text-[11px] text-gray-400 hover:text-gray-600 underline underline-offset-2"
            >
              Reconnect Google account
            </button>
          </div>

          <button
            onClick={openCreateModal}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-white font-medium shadow-sm hover:shadow transition-all duration-200"
            style={{ backgroundColor: brand.teal }}
          >
            <Plus className="w-5 h-5" />
            New Event
          </button>
        </div>
      </div>

      {/* Stale-connection banner: Google token expired/revoked, so events can't sync */}
      {needsReconnect && (
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-2 text-amber-800">
            <RefreshCw className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <span className="text-sm">
              Your Google Calendar connection has expired, so new events aren't syncing.
              Reconnect to start syncing again.
            </span>
          </div>
          <button
            onClick={handleConnectCalendar}
            className="self-start sm:self-auto px-4 py-2 rounded-lg text-white text-sm font-medium whitespace-nowrap"
            style={{ backgroundColor: brand.teal }}
          >
            Reconnect Google Calendar
          </button>
        </div>
      )}

      {/* Calendar Controls */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6 mb-6 w-full max-w-full overflow-hidden">
        <div className="flex flex-col lg:flex-row justify-between items-center gap-4 mb-4">
          {/* Navigation */}
          <div className="flex items-center justify-between w-full lg:w-auto gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (view === "month") navigateMonth(-1);
                  else if (view === "week") navigateWeek(-1);
                  else navigateDay(-1);
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Previous"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <button
                onClick={goToToday}
                className="px-3 sm:px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors text-sm sm:text-base"
              >
                Today
              </button>

              <button
                onClick={() => {
                  if (view === "month") navigateMonth(1);
                  else if (view === "week") navigateWeek(1);
                  else navigateDay(1);
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <h3 className="text-lg sm:text-xl font-semibold text-center lg:text-left flex-1 lg:flex-none">
              {viewTitle}
            </h3>
          </div>

          {/* View Switcher */}
          <div className="flex gap-1 sm:gap-2 w-full lg:w-auto bg-gray-100 p-1 rounded-lg">
            {["month", "week", "day"].map((viewType) => (
              <button
                key={viewType}
                onClick={() => setView(viewType)}
                className={`flex-1 lg:flex-none px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors duration-200 whitespace-nowrap ${view === viewType
                  ? "text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                  }`}
                style={view === viewType ? { backgroundColor: brand.teal } : {}}
              >
                {viewType.charAt(0).toUpperCase() + viewType.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Month View */}
        {view === "month" && (
          <div>
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className="text-center font-semibold text-gray-700 py-2 text-xs sm:text-sm">
                  <span className="hidden sm:inline">{day}</span>
                  <span className="sm:hidden">{day.charAt(0)}</span>
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {monthGrid.map((day, index) => {
                const dayEvents = day ? getEventsForDay(day) : [];
                const isToday = day &&
                  day === new Date().getDate() &&
                  currentDate.getMonth() === new Date().getMonth() &&
                  currentDate.getFullYear() === new Date().getFullYear();

                return (
                  <div
                    key={index}
                    onClick={() => day && openDayModal(day)}
                    role={day ? "button" : undefined}
                    tabIndex={day ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (day && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        openDayModal(day);
                      }
                    }}
                    className={`min-h-[60px] sm:min-h-[100px] p-1 sm:p-2 rounded-lg border transition-colors ${day ? "bg-white hover:bg-gray-100 hover:border-gray-300 cursor-pointer" : "bg-gray-50"
                      } ${isToday ? "ring-2" : ""}`}
                    style={isToday ? { borderColor: brand.teal, ringColor: brand.teal } : {}}
                  >
                    {day && (
                      <>
                        <div className={`text-xs sm:text-sm font-medium mb-1 ${isToday ? "font-bold" : ""}`}
                          style={isToday ? { color: brand.teal } : {}}>
                          {day}
                        </div>
                        <div className="space-y-1">
                          {dayEvents.slice(0, 3).map((event) => {
                            const evColor = getEventColor(event);
                            return (
                            <div
                              key={event.id}
                              className="text-[10px] sm:text-xs p-0.5 sm:p-1 rounded truncate mb-0.5 pointer-events-none"
                              style={{ backgroundColor: `${evColor}20`, color: evColor }}
                            >
                              <div className="font-medium truncate">{event.title}</div>
                              <div className="hidden md:block text-[10px] opacity-75">{formatTime(event.start)}</div>
                            </div>
                            );
                          })}

                          {dayEvents.length > 3 && (
                            <div className="text-[9px] sm:text-[10px] text-gray-500 text-center sm:text-left">
                              +{dayEvents.length - 3} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Week / Day list view — events filtered to the selected period */}
        {(view === "week" || view === "day") && (() => {
          const listEvents = getListEvents();
          return (
          <div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading events...</div>
            ) : listEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No events scheduled for this {view === "day" ? "day" : "week"}
              </div>
            ) : (
              listEvents.map((event) => {
                const evColor = getEventColor(event);
                return (
                <div
                  key={event.id}
                  className="flex flex-row items-center gap-3 p-3 sm:p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-all duration-200 cursor-pointer"
                  onClick={() => openViewModal(event)}
                >
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${evColor}20` }}
                  >
                    <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: evColor }} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm sm:text-lg font-medium text-gray-900 truncate">{event.title}</h4>
                    <div className="mt-0.5 sm:mt-1 flex flex-wrap gap-x-3 gap-y-1">
                      <div className="flex items-center text-xs sm:text-sm text-gray-600">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                        <span className="truncate">{formatTime(event.start)} - {formatTime(event.end)}</span>
                      </div>
                      {event.location && (
                        <div className="flex items-center text-xs sm:text-sm text-gray-600">
                          <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                          <span className="truncate max-w-[100px] sm:max-w-none">{event.location}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {isEventEditable(event) && (
                    <div className="flex space-x-1 sm:space-x-2 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(event);
                        }}
                        className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <Edit2 className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteEvent(event);
                        }}
                        className="p-1.5 sm:p-2 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  )}
                </div>
                );
              })
            )}
          </div>
          );
        })()}
      </div>

      {/* Day Events Modal — opened by clicking a date in the month grid */}
      {dayModalDate && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setDayModalDate(null)}
        >
          <div
            className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                {dayModalDate.toLocaleDateString('en-US', {
                  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                })}
              </h3>
              <button
                onClick={() => setDayModalDate(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {(() => {
              const dayEvents = getEventsForDate(dayModalDate);
              return dayEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No events on this day</div>
              ) : (
                <ul className="space-y-2">
                  {dayEvents.map((event) => {
                    const evColor = getEventColor(event);
                    return (
                      <li
                        key={event.id}
                        onClick={() => {
                          setDayModalDate(null);
                          openViewModal(event);
                        }}
                        className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: evColor }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{event.title}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">
                              {formatTime(event.start)} - {formatTime(event.end)}
                              {event.location ? ` • ${event.location}` : ""}
                            </span>
                          </div>
                        </div>
                        {!isEventEditable(event) && (
                          <span className="text-[10px] text-gray-400 flex-shrink-0">Read-only</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              );
            })()}

            <div className="flex pt-4">
              <button
                onClick={() => {
                  const d = dayModalDate;
                  setDayModalDate(null);
                  openCreateModalOnDate(d);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-white font-medium"
                style={{ backgroundColor: brand.teal }}
              >
                <Plus className="w-4 h-4" />
                Add Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold">
                {modalMode === "create" ? "Create Event" : modalMode === "edit" ? "Edit Event" : "Event Details"}
              </h3>
              <button
                onClick={() => setShowEventModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* View Mode */}
            {modalMode === "view" && selectedEvent && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <div className="text-lg font-semibold">{selectedEvent.title}</div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                  <div className="flex items-center gap-2 text-gray-900">
                    <Clock className="w-4 h-4" />
                    {formatTime(selectedEvent.start)} - {formatTime(selectedEvent.end)}
                  </div>
                </div>

                {selectedEvent.location && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <div className="flex items-center gap-2 text-gray-900">
                      <MapPin className="w-4 h-4" />
                      {selectedEvent.location}
                    </div>
                  </div>
                )}

                {selectedEvent.description && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <div className="text-gray-900">{selectedEvent.description}</div>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => handleCreateTaskFromEvent(selectedEvent)}
                    className="flex-1 px-4 py-2 rounded-xl font-medium border"
                    style={{ borderColor: brand.teal, color: brand.teal }}
                  >
                    Create Task
                  </button>
                  {isEventEditable(selectedEvent) && (
                    <>
                      <button
                        onClick={() => openEditModal(selectedEvent)}
                        className="flex-1 px-4 py-2 rounded-xl font-medium text-white"
                        style={{ backgroundColor: brand.teal }}
                      >
                        Edit Event
                      </button>
                      <button
                        onClick={() => handleDeleteEvent(selectedEvent)}
                        className="px-4 py-2 rounded-xl font-medium border border-red-200 text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </>
                  )}
                  {!isEventEditable(selectedEvent) && (
                    <span className="flex-1 self-center text-right text-sm text-gray-400">
                      Read-only
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Create/Edit Mode */}
            {(modalMode === "create" || modalMode === "edit") && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Event Title *
                  </label>
                  <input
                    type="text"
                    value={eventForm.summary}
                    onChange={(e) => setEventForm({ ...eventForm, summary: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                    style={{ focusRingColor: brand.teal }}
                    placeholder="Event title"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={eventForm.start}
                      onChange={(e) => setEventForm({ ...eventForm, start: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (minutes)
                    </label>
                    <input
                      type="number"
                      value={eventForm.duration_minutes}
                      onChange={(e) => setEventForm({ ...eventForm, duration_minutes: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      min="15"
                      step="15"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={eventForm.location}
                    onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="Event location"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                    rows="3"
                    placeholder="Event description"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={modalMode === "create" ? handleCreateEvent : handleUpdateEvent}
                    disabled={submitting}
                    className={`flex-1 px-4 py-2 rounded-xl font-medium text-white ${
                      submitting ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                    style={{ backgroundColor: brand.teal }}
                  >
                    {submitting
                      ? modalMode === "create"
                        ? "Creating…"
                        : "Updating…"
                      : modalMode === "create"
                      ? "Create Event"
                      : "Update Event"}
                  </button>
                  <button
                    onClick={() => setShowEventModal(false)}
                    disabled={submitting}
                    className="px-4 py-2 rounded-xl font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
